import { useEffect, useRef } from 'react';
import { AppState } from 'react-native';
import { useRouter } from 'expo-router';
import { useAuthStore } from '@/store';
import { isNativePushPlatform, registerDevicePushToken } from '@/lib/pushNotifications';
import { notificationDestination } from '@/lib/notificationDestination';
import { nativeAlertKeys, nativeNotificationEnabled } from '@/lib/nativeNotificationSettings';

export default function usePushNotifications() {
  const router = useRouter();
  const token = useAuthStore((state) => state.token);
  const isAuthenticated = Boolean(token);
  const userId = useAuthStore((state) => state.user?.id);
  const sessionGeneration = useAuthStore((state) => state._sessionGeneration);
  const isGhost = useAuthStore((state) => state.user?.flags?.isGhost);
  const alertsEnabled = useAuthStore(state => nativeAlertKeys
    .some(key => nativeNotificationEnabled(state.user?.preferences?.nativeNotifications, key)));
  const responseSub = useRef(null);
  const lastHandledId = useRef(null);

  useEffect(() => {
    if (!isNativePushPlatform() || !isAuthenticated || !userId || isGhost || !alertsEnabled) return undefined;
    let cancelled = false;
    let registering = false;
    const isCurrent = () => !cancelled && useAuthStore.getState().user?.id === userId
      && useAuthStore.getState()._sessionGeneration === sessionGeneration
      && Boolean(useAuthStore.getState().token);

    const register = async requestPermissions => {
      if (registering || !isCurrent()) return;
      registering = true;
      try {
        await registerDevicePushToken({ isCurrent, requestPermissions });
      } catch (error) {
        if (!cancelled) {
          console.warn('[push] register failed', error?.message || error);
        }
      }
      finally { registering = false; }
    };
    void register(true);
    // Returning from system settings should finish registration, never reopen
    // a denied permission prompt or require another sign-in.
    const appState = AppState.addEventListener('change', state => {
      if (state === 'active') void register(false);
    });

    return () => {
      cancelled = true;
      appState.remove();
    };
  }, [isAuthenticated, userId, sessionGeneration, isGhost, alertsEnabled]);

  useEffect(() => {
    if (!isNativePushPlatform() || !isAuthenticated || !userId || isGhost) return undefined;

    let isMounted = true;
    const openFromResponse = (response) => {
      if (!isMounted || useAuthStore.getState().user?.id !== userId
        || useAuthStore.getState()._sessionGeneration !== sessionGeneration
        || !useAuthStore.getState().token) return;
      const id = response?.notification?.request?.identifier;
      if (id && lastHandledId.current === id) return;
      if (id) lastHandledId.current = id;
      const href = notificationDestination(response?.notification, userId);
      if (href) router.push(href);
    };

    (async () => {
      const Notifications = await import('expo-notifications');
      if (!isMounted) return;
      Notifications.setNotificationHandler({
        handleNotification: async () => ({
          shouldShowAlert: false,
          shouldPlaySound: false,
          shouldSetBadge: false,
          shouldShowBanner: false,
          shouldShowList: false,
        }),
      });

      responseSub.current = Notifications.addNotificationResponseReceivedListener((response) => {
        openFromResponse(response);
      });
      const last = await Notifications.getLastNotificationResponseAsync();
      if (isMounted) {
        openFromResponse(last);
        if (last) await Notifications.clearLastNotificationResponseAsync?.();
      }
    })().catch((error) => {
      console.warn('[push] listener setup failed', error?.message || error);
    });

    return () => {
      isMounted = false;
      responseSub.current?.remove?.();
      responseSub.current = null;
    };
  }, [router, isAuthenticated, userId, sessionGeneration, isGhost]);
}
